import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ColDef, GridApi } from "ag-grid-community";
import { usePreviousDifferent } from "rooks";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material";
import { ResourcesAgGrid } from "./components/grids/ResourcesAgGrid";
import {
  getResourceGridSize,
  resourceMasterGridMaxSize,
} from "./utils/constants/sizes";
import type { RowGroupOpenedEvent } from "ag-grid-community/dist/types/src/events";
import { ResourceType } from "@/services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { gridResourceDataSelector } from "@/store/resources-grid/selectors/resource-data/gridResourceDataSelector";
import {
  addNewGridInResourcesGridSlice,
  removeGridByIdResourcesGridSlice,
  setGridResourceDataPayload,
} from "@/store/resources-grid/resourcesGridSlice";
import { updateGridResourceDataThunk } from "@/store/resources-grid/thunks/data/updateGridResourceDataThunk";
import { gridDetailRowsHeightsSelector } from "@/store/resources-grid/selectors/grid/gridDetailRowsHeightsSelector";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { ResourcesGridResourcePayloadType } from "@/store/resources-grid/utils/types/common";
import {
  ResourcesAgGridConfigs,
  ResourcesAgGridHiddenActions,
} from "@/components/pages/common/resources-ag-grid/utils/types/common";

interface ResourcesMasterGridProps extends ResourcesAgGridConfigs {
  notFullHeight?: boolean;
  resourceType: ResourceType;
  gridApi: GridApi | null;
  masterGridId: string;
  gridMaxHeight?: number;
  masterResourceType: ResourceType;
  setGridApi: Dispatch<SetStateAction<GridApi | null>>;
  dataToFetchResources?: ResourcesGridResourcePayloadType;
  overrideGridData?: AllTypeResourcesData[];
  agGridsWrapperStyles?: SxProps<Theme>;
  hiddenActions?: ResourcesAgGridHiddenActions;
  additionalColumns?: ColDef[];
}

export const ResourcesMasterGrid: FC<ResourcesMasterGridProps> = ({
  resourceType,
  dataToFetchResources,
  notFullHeight,
  setGridApi,
  masterGridId,
  gridApi,
  masterResourceType,
  overrideGridData,
  agGridsWrapperStyles,
  hideRowActions,
  hideSelection,
  hideExpand,
  preventDetailsDrawerOpener,
  gridMaxHeight,
  hiddenActions,
  additionalColumns,
}) => {
  const dispatch = useAppDispatch();
  const [masterRowsHeights, setMasterRowsHeights] = useState<
    Record<string, number>
  >({});

  const data = useAppSelector((state) =>
    gridResourceDataSelector(state, masterGridId),
  );

  const detailRowsHeights = useAppSelector((state) =>
    gridDetailRowsHeightsSelector(state, masterGridId),
  );

  const prevData = usePreviousDifferent(data);
  const gridData = data ?? prevData;

  const resourcesLength =
    overrideGridData?.length ?? gridData?.resources?.length ?? 0;

  const wrapperStyles = useMemo(() => {
    if (!notFullHeight) {
      return agGridsWrapperStyles;
    }

    const expandedMasterRowsHeights = Object.values(
      masterRowsHeights ?? {},
    ).reduce((sum, height) => sum + height, 0);
    const expandedDetailRowsHeights = Object.values(
      detailRowsHeights ?? {},
    ).reduce((sum, height) => sum + height, 0);

    const maxHeight = gridMaxHeight || resourceMasterGridMaxSize;

    const gridHeight = Math.min(
      getResourceGridSize(resourcesLength, true) +
        expandedDetailRowsHeights +
        expandedMasterRowsHeights,
      maxHeight,
    );

    return {
      height: gridHeight,
      minHeight: 150,
      ...(agGridsWrapperStyles ?? {}),
    };
  }, [
    agGridsWrapperStyles,
    notFullHeight,
    masterRowsHeights,
    detailRowsHeights,
    resourcesLength,
    gridMaxHeight,
  ]);

  const onChildRowGroupOpened = useCallback(
    (params: RowGroupOpenedEvent) => {
      if (!notFullHeight) {
        return;
      }

      const expanded = params.expanded;
      const id = params.data?.id;
      const childrenCount = params.data?.child_resource?.resources?.length;
      const height = expanded ? getResourceGridSize(childrenCount) : 0;

      setMasterRowsHeights((obj) => ({ ...obj, [id]: height }));
    },
    [notFullHeight],
  );

  useEffect(() => {
    dispatch(
      addNewGridInResourcesGridSlice({
        [masterGridId]: { resourceType },
      }),
    );

    return () => {
      dispatch(removeGridByIdResourcesGridSlice(masterGridId));
    };
  }, [dispatch, resourceType, masterGridId]);

  useEffect(() => {
    if (!dataToFetchResources) {
      return;
    }

    dispatch(
      setGridResourceDataPayload({
        gridId: masterGridId,
        payload: dataToFetchResources,
      }),
    );
    dispatch(updateGridResourceDataThunk(masterGridId));
  }, [dispatch, resourceType, masterGridId, dataToFetchResources]);

  if (!gridData && !overrideGridData) {
    return null;
  }

  return (
    <ResourcesAgGrid
      api={gridApi}
      parentRowId="master"
      hideExpand={hideExpand}
      gridApiSetter={setGridApi}
      resourceType={resourceType}
      masterGridId={masterGridId}
      hiddenActions={hiddenActions}
      wrapperStyles={wrapperStyles}
      hideSelection={hideSelection}
      hideRowActions={hideRowActions}
      additionalColumns={additionalColumns}
      masterResourceType={masterResourceType}
      onRowGroupOpened={onChildRowGroupOpened}
      rowData={overrideGridData ?? gridData?.resources}
      preventDetailsDrawerOpener={preventDetailsDrawerOpener}
    />
  );
};
